import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from 'react';
import { Table, Input, FormGroup, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFolderOpen, faPenToSquare, faTrashCan } from '@fortawesome/free-solid-svg-icons';

import { translations } from './../../i18/translations.js';
import Search from './../../util/Search.js';
import Pager from './../../util/Pager.js';
import { Space } from './../../util/Html.js';
import ConfirmDialog from './../../util/ConfirmDialog.js';

const ContentManagement = () => {

    const API_URL = process.env.REACT_APP_SERVICE_API_URL;
    const {user, lang, showAlert, checkAccess} = useOutletContext();
    const {parentId} = useParams();
    const navigate = useNavigate();

    const [query, setQuery] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [showOld, setShowOld] = useState(false);
    const [selectedParentId, setSelectedParentId] = useState(parentId);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [template, setTemplate] = useState('');
    const [pager, setPager] = useState([]);

    const [parentList, setParentList] = useState([]);
    const [contents, setContents] = useState([]);
    const [selectedContent, setSelectedContent] = useState(null);

    const pageSize = 20;

    useEffect(() => {
        fetchContents(pageIndex, query, showOld, selectedParentId);
    }, [pageIndex, query, showOld, selectedParentId, lang, user]);

    const toggleConfirmDialog = () => setConfirmOpen(!confirmOpen);

    const fetchContents = async (pageIndex, query, showOld, selectedParentId) => {
        try {
            const payload = {
                filter: {
                    pageSize: 20,
                    pageIndex: pageIndex,
                    queryString: query,
                    showOld: showOld,
                    parentId: selectedParentId
                }
            };

            const response = await fetch(`${API_URL}/admin/get_content_data.php`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (checkAccess(data)) {
                setContents(data.data);
                setPager(data.pagerButtonIndexes);
                setTemplate(data.template);
                setParentList(data.parentList);
            }
        } catch (error) {
            console.error("Error fetching contents:", error);
        }
    };

    const openContent = (id) => {
        setSelectedParentId(id);
    };

    const editContent = (id) => {
        navigate("/ContentManagementEdit/" + selectedParentId + "/" + id);
    };

    const handleDeleteContentClick = async (selectedContent) => {
        setSelectedContent(selectedContent);
        toggleConfirmDialog();
    };


    const deleteContent = async () => {
        try {
            const payload = {
                content: {
                    id: selectedContent.id
                }
            };

            const response = await fetch(`${API_URL}/admin/delete_content.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (checkAccess(data)) {
                showAlert(data.alert);
                fetchContents(pageIndex, query, showOld, selectedParentId);
                toggleConfirmDialog();
            }
        } catch (error) {
            console.error("Error fetching members:", error);
        }
    };

    const handleShowOldSwitch = (e) => {
        const {checked} = e.target;
        setShowOld(checked);
    };

    return (
            <div className="mbkegy-content">
                <h1 className="mbkegy-content">{translations[lang].content_management}</h1>
            
                {parentList.map((p) => (
                                <>
                                &nbsp;/&nbsp;<Button className="text-nowrap" color="link" onClick={() => setSelectedParentId(p.id)}>{p.label}</Button>
                                </>
                                ))}
            
                <div className="membership-management-filter-container mb-3">
                    <Search placeholder={translations[lang].search_content} handleSearch={setQuery} />
                    <Button className="text-nowrap" color="success" title={translations[lang].anew} onClick={() => editContent(-1)}>
                        <FontAwesomeIcon icon={faPlus} />
                    </Button>
                    <FormGroup switch>
                        <Input id="showOld" type="switch" checked={showOld} onChange={handleShowOldSwitch} />
                        <Label for="showOld" className="text-nowrap">{translations[lang].show_invisible}</Label>
                    </FormGroup>
            
                </div>
            
                <Table striped className="mbkegy-table">
                    <thead>
                        <tr>
                            <th style={{width: '75px'}}>{translations[lang].col_id}</th>
                            <th style={{width: '65%'}}>{translations[lang].col_content}</th>
                            <th style={{width: '200px'}}>{translations[lang].col_expiration}</th>
                            <th style={{width: '100px'}}>{translations[lang].col_feature}</th>
                            <th style={{width: '110px'}}>&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {contents.map((c) => (
                                <tr key={c.id}>
                                    <td>{c.description ? c.id : <Space />}</td>
                            <td>{c.description || <Space />}</td>
                            <td>{c.expired_time || <Space />}</td>
                            <td>{c.access_feature || <Space />}</td>
                            <td className="text-nowrap">
                                {c.description && <Button outline size="sm" title={translations[lang].anew} onClick={() => openContent(c.id)}><FontAwesomeIcon icon={faFolderOpen} /></Button>}
                                {c.description && <Button outline size="sm" color="primary" title={translations[lang].anew} onClick={() => editContent(c.id)}><FontAwesomeIcon icon={faPenToSquare} /></Button>}
                                {c.description && <Button outline size="sm" color="danger" title={translations[lang].anew} onClick={() => handleDeleteContentClick(c)}><FontAwesomeIcon icon={faTrashCan} /></Button>}
                            </td>
                            </tr>
                                    ))}
                    </tbody>
                </Table>
            
                <Pager template={`${translations[lang].rows}: ${template}`} pager={pager} handlePageChange={setPageIndex} />
            
                <ConfirmDialog 
                    title={translations[lang].content_delete}
                    yes={translations[lang].yes}
                    no={translations[lang].cancel}
                    isOpen={confirmOpen} 
                    toggle={toggleConfirmDialog} 
                    onConfirm={deleteContent} 
                    message={translations[lang].content_delete_confirm}
                    />
            
            </div>
            );
};

export default ContentManagement;